import React, { useState, useEffect, useRef } from 'react';
import { signInAnonymously } from 'firebase/auth';
import { collection, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { notifyIntake } from '../services/intakeService';

// Shared access code you give to dentists/labs. Change anytime.
const ACCESS_CODE = 'ARCHX2026';

export default function SendFiles() {
  const [form, setForm] = useState({
    senderName: '', senderEmail: '', practice: '', caseRef: '', notes: '',
  });
  const [files, setFiles]       = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus]     = useState('idle'); // idle | uploading | done | error
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [unlocked, setUnlocked]   = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeErr, setCodeErr]     = useState('');
  const [reqEmail, setReqEmail]   = useState('');
  const [reqStatus, setReqStatus] = useState(''); // '' | 'sending' | 'sent' | 'error'
  const inputRef = useRef();

  const requestCode = async (e) => {
    e.preventDefault();
    if (!reqEmail.trim()) { setReqStatus('error'); return; }
    setReqStatus('sending');
    try {
      const res = await fetch('https://archxai.io/api/request-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: reqEmail.trim() }),
      });
      setReqStatus(res.ok ? 'sent' : 'error');
    } catch {
      setReqStatus('error');
    }
  };

  const submitCode = (e) => {
    e.preventDefault();
    if (codeInput.trim().toUpperCase() === ACCESS_CODE.toUpperCase()) {
      setUnlocked(true); setCodeErr('');
    } else {
      setCodeErr('That code isn’t right. Check with your ARCHX contact.');
    }
  };

  // Sign in anonymously so uploads pass Storage/Firestore auth rules
  useEffect(() => {
    if (!auth.currentUser) signInAnonymously(auth).catch(() => {});
  }, []);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const addFiles = (list) => {
    const incoming = Array.from(list);
    setFiles(prev => [...prev, ...incoming]);
  };
  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const ready =
    form.senderName.trim() && form.senderEmail.trim() &&
    form.practice.trim() && files.length > 0 && status !== 'uploading';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setStatus('uploading');
    setErrorMsg('');
    try {
      if (!auth.currentUser) await signInAnonymously(auth);

      // 1. Create the intake record up front to get an ID
      const docRef = await addDoc(collection(db, 'intake'), {
        ...form,
        status: 'new',
        ownerUid: auth.currentUser ? auth.currentUser.uid : null,
        fileCount: files.length,
        files: [],
        createdAt: serverTimestamp(),
      });

      // 2. Upload each file under intake/{id}/
      const uploaded = [];
      const total = files.length;
      for (let idx = 0; idx < total; idx++) {
        const file = files[idx];
        const doneSoFar = idx;
        const path = `intake/${docRef.id}/${file.name}`;
        const task = uploadBytesResumable(ref(storage, path), file);
        // eslint-disable-next-line no-loop-func
        await new Promise((resolve, reject) => {
          task.on('state_changed',
            (snap) => {
              const pct = (doneSoFar + snap.bytesTransferred / snap.totalBytes) / total;
              setProgress(Math.round(pct * 100));
            },
            reject,
            async () => {
              const url = await getDownloadURL(task.snapshot.ref).catch(() => null);
              uploaded.push({ name: file.name, path, url, size: file.size });
              resolve();
            }
          );
        });
      }

      // 3. Save file list on the record
      await updateDoc(docRef, { files: uploaded });

      // 4. Email notification to ArchX (best-effort)
      notifyIntake({ id: docRef.id, ...form, files: uploaded }).catch(() => {});

      setProgress(100);
      setStatus('done');
    } catch (err) {
      setErrorMsg(err.message || 'Upload failed. Please try again.');
      setStatus('error');
    }
  };

  if (!unlocked) {
    return (
      <Shell>
        <h1 style={{ color: '#fff', fontSize: '22px', margin: '0 0 6px' }}>Enter your access code</h1>
        <p style={{ color: '#94a3b8', margin: '0 0 20px', fontSize: '14px' }}>
          Your ARCHX contact gave you a code to send files. Enter it below to continue.
        </p>
        <form onSubmit={submitCode}>
          <input value={codeInput} onChange={(e) => setCodeInput(e.target.value)} style={inp} placeholder="Access code" autoFocus />
          {codeErr && <div style={{ color: '#f87171', fontSize: '13px', marginTop: '10px' }}>{codeErr}</div>}
          <button type="submit" style={{ ...btnGold, marginTop: '18px', width: '100%' }}>Continue</button>
        </form>

        <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #262626' }}>
          {reqStatus === 'sent' ? (
            <p style={{ color: '#22c55e', fontSize: '13px', margin: 0, textAlign: 'center' }}>
              ✓ Request sent. The ARCHX team will email you an access code shortly.
            </p>
          ) : (
            <>
              <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 10px' }}>
                Don&rsquo;t have a code? Enter your email and we&rsquo;ll send you one.
              </p>
              <form onSubmit={requestCode}>
                <input
                  type="email"
                  value={reqEmail}
                  onChange={(e) => setReqEmail(e.target.value)}
                  style={inp}
                  placeholder="Your email"
                />
                {reqStatus === 'error' && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '8px' }}>
                    Please enter a valid email and try again.
                  </div>
                )}
                <button
                  type="submit"
                  disabled={reqStatus === 'sending'}
                  style={{
                    marginTop: '12px', width: '100%', padding: '11px 20px', borderRadius: '8px',
                    border: '1px solid #c8922a', background: 'transparent', color: '#c8922a',
                    fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                    opacity: reqStatus === 'sending' ? 0.6 : 1,
                  }}
                >
                  {reqStatus === 'sending' ? 'Sending…' : 'Request a code'}
                </button>
              </form>
            </>
          )}
        </div>
      </Shell>
    );
  }

  if (status === 'done') {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <div style={{ fontSize: '46px', marginBottom: '12px' }}>✓</div>
          <h2 style={{ margin: '0 0 8px', color: '#fff' }}>Files received</h2>
          <p style={{ color: '#94a3b8', maxWidth: '440px', margin: '0 auto 24px' }}>
            Thanks, {form.senderName.split(' ')[0]}. Your {files.length} file{files.length !== 1 ? 's' : ''} reached
            the ARCHX AI team. We'll take it from here and follow up at {form.senderEmail}.
          </p>
          <button onClick={() => { setForm({ senderName:'', senderEmail:'', practice:'', caseRef:'', notes:'' }); setFiles([]); setStatus('idle'); setProgress(0); }}
            style={btnGold}>Send more files</button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={{ color: '#fff', fontSize: '24px', margin: '0 0 6px' }}>Send your case files to ARCHX AI</h1>
      <p style={{ color: '#94a3b8', margin: '0 0 24px', fontSize: '14px' }}>
        For dentists and labs — drop your scans and files below and we'll load them into your case. No account needed.
      </p>

      <form onSubmit={handleSubmit}>
        <div style={grid2}>
          <Field label="Your name *"><input name="senderName" value={form.senderName} onChange={change} style={inp} placeholder="Dr. Jane Smith" /></Field>
          <Field label="Email *"><input name="senderEmail" type="email" value={form.senderEmail} onChange={change} style={inp} placeholder="you@practice.com" /></Field>
          <Field label="Practice / lab name *"><input name="practice" value={form.practice} onChange={change} style={inp} placeholder="Smith Dental Lab" /></Field>
          <Field label="Patient / case reference"><input name="caseRef" value={form.caseRef} onChange={change} style={inp} placeholder="e.g. Tranchida, upper arch" /></Field>
        </div>
        <Field label="Notes"><textarea name="notes" value={form.notes} onChange={change} rows={3} style={{ ...inp, resize: 'vertical' }} placeholder="Anything we should know about this case…" /></Field>

        {/* Dropzone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current && inputRef.current.click()}
          style={{
            border: `2px dashed ${dragOver ? '#C09422' : '#334155'}`,
            borderRadius: '12px', padding: '28px', textAlign: 'center',
            cursor: 'pointer', background: dragOver ? 'rgba(192,148,34,0.06)' : 'transparent',
            marginTop: '8px', transition: 'all 0.2s',
          }}>
          <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => addFiles(e.target.files)} />
          <div style={{ color: '#cbd5e1', fontSize: '14px' }}>
            <strong style={{ color: '#C09422' }}>Click to browse</strong> or drag files here
          </div>
          <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>STL, scans, photos, ZIPs — any file type</div>
        </div>

        {files.length > 0 && (
          <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {files.map((f, i) => (
              <div key={i} style={fileRow}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                <button type="button" onClick={() => removeFile(i)} style={removeBtn} aria-label="Remove">×</button>
              </div>
            ))}
          </div>
        )}

        {status === 'uploading' && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ height: '6px', background: '#334155', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, background: '#C09422', transition: 'width 0.2s' }} />
            </div>
            <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '6px' }}>Uploading… {progress}%</div>
          </div>
        )}

        {errorMsg && <div style={{ color: '#f87171', fontSize: '13px', marginTop: '12px' }}>{errorMsg}</div>}

        <button type="submit" disabled={!ready} style={{ ...btnGold, marginTop: '20px', width: '100%', opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'not-allowed' }}>
          {status === 'uploading' ? 'Sending…' : 'Send files to ARCHX AI'}
        </button>
      </form>
    </Shell>
  );
}

// ── Layout + styles ──────────────────────────────────────────────
function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0A0A0A, #111)', padding: '40px 16px' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="https://archxai-portal.web.app/logo.png" alt="ARCHX AI" style={{ height: '70px' }} />
        </div>
        <div style={{ background: 'linear-gradient(135deg, #0d0d0d, #171717)', border: '1px solid rgba(192,148,34,0.2)', borderRadius: '18px', padding: '28px' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label style={{ display: 'block', marginBottom: '14px' }}>
      <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '6px' }}>{label}</div>
      {children}
    </label>
  );
}
const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 16px' };
const inp = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155', background: '#0b0b0b', color: '#fff', fontSize: '14px', outline: 'none' };
const btnGold = { background: '#C09422', color: '#000', border: 'none', borderRadius: '8px', padding: '12px 22px', fontWeight: 700, fontSize: '14px' };
const fileRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', background: '#0b0b0b', border: '1px solid #1f2937', borderRadius: '8px', padding: '8px 12px', color: '#cbd5e1', fontSize: '13px' };
const removeBtn = { background: 'none', border: 'none', color: '#f87171', fontSize: '18px', cursor: 'pointer', lineHeight: 1 };
